export interface UploadedDocument {
  uploadedDocumentId: string;
  fileName: string;
  originalFileName: string;
  mimeType: string;
  fileSizeBytes: number;
  blobUri: string;
  searchIndexDocumentId: string | null;
  targetIndexId: string;
  status: number;
  statusMessage: string | null;
  uploadBatchId: string;
  category: string | null;
  description: string | null;
  uploadedByUserId: string;
  uploadedAt: string;
  indexedAt: string | null;
  lastReindexedAt: string | null;
}

export interface DocumentCategory {
  documentCategoryId: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface SearchIndexInfo {
  id: string;
  displayName: string;
  description: string;
}

export interface UploadCheckResult {
  fileName: string;
  isDuplicate: boolean;
  existingDocumentId: string | null;
}

export async function getAvailableIndexes(): Promise<SearchIndexInfo[]> {
  const response = await fetch("/api/useradmin/documents/indexes");
  if (!response.ok) throw new Error("Failed to fetch indexes");
  const data = await response.json();
  return data.indexes ?? data;
}

export interface UploadConflict {
  originalFileName: string;
  existingId: string;
  uploadedAt: string;
  category: string | null;
}

export async function checkUploadDuplicates(fileNames: string[], targetIndexId: string): Promise<UploadConflict[]> {
  const response = await fetch("/api/useradmin/documents/upload/check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileNames, targetIndexId }),
  });
  if (!response.ok) throw new Error("Failed to check duplicates");
  const data = await response.json();
  return data.conflicts ?? [];
}

export async function uploadDocuments(
  files: File[],
  targetIndexId: string,
  category?: string,
  description?: string
): Promise<{ batchId: string; documents: UploadedDocument[] }> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  formData.append("targetIndexId", targetIndexId);
  if (category) formData.append("category", category);
  if (description) formData.append("description", description);

  // In dev mode, bypass SWA CLI proxy (which has body size issues with multipart uploads)
  // by sending directly to Vite dev server, forwarding the SWA auth header manually
  let uploadUrl = "/api/useradmin/documents/upload";
  const headers: Record<string, string> = {};

  if (import.meta.env.DEV) {
    try {
      // Get auth principal from SWA CLI
      const authRes = await fetch("/.auth/me");
      if (authRes.ok) {
        const authData = await authRes.json();
        if (authData.clientPrincipal) {
          const principalB64 = btoa(JSON.stringify(authData.clientPrincipal));
          headers["x-ms-client-principal"] = principalB64;
        }
      }
    } catch {
      // Fall through to normal upload if auth fetch fails
    }
    uploadUrl = "http://localhost:7227/api/useradmin/documents/upload";
  }

  const response = await fetch(uploadUrl, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Upload failed");
  }
  return response.json();
}

export async function listDocuments(page = 1, pageSize = 20, status?: number, targetIndexId?: string, search?: string): Promise<{
  documents: UploadedDocument[];
  total: number;
  page: number;
  pageSize: number;
}> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (status !== undefined) params.set("status", String(status));
  if (targetIndexId) params.set("targetIndexId", targetIndexId);
  if (search) params.set("search", search);
  const response = await fetch(`/api/useradmin/documents?${params}`);
  if (!response.ok) throw new Error("Failed to list documents");
  const data = await response.json();
  return { documents: data.documents, total: data.totalCount ?? data.total, page: data.page, pageSize: data.pageSize };
}

export async function deleteDocument(id: string): Promise<void> {
  const response = await fetch(`/api/useradmin/documents/${id}`, { method: "DELETE" });
  if (!response.ok) throw new Error("Failed to delete document");
}

export async function reindexDocument(id: string): Promise<void> {
  const response = await fetch(`/api/useradmin/documents/reindex/${id}`, { method: "POST" });
  if (!response.ok) throw new Error("Failed to reindex document");
}

export async function startIngestion(batchId: string): Promise<{ instanceId: string }> {
  const response = await fetch(`/api/useradmin/documents/ingest/${batchId}`, { method: "POST" });
  if (!response.ok) throw new Error("Failed to start ingestion");
  return response.json();
}

export async function getIngestionStatus(instanceId: string): Promise<{
  instanceId: string;
  runtimeStatus: string;
  createdAt: string;
  lastUpdatedAt: string;
  output: string | null;
}> {
  const response = await fetch(`/api/useradmin/documents/ingest/status/${instanceId}`);
  if (!response.ok) throw new Error("Failed to get ingestion status");
  return response.json();
}

export interface BatchStatusDocument {
  id: string;
  fileName: string;
  status: string;
  statusMessage: string | null;
  fileSizeBytes: number;
}

export interface BatchStatus {
  batchId: string;
  overallStatus: "Processing" | "Completed" | "PartialFailure";
  documents: BatchStatusDocument[];
}

export async function getBatchStatus(batchId: string): Promise<BatchStatus> {
  const response = await fetch(`/api/useradmin/documents/batch/${batchId}/status`);
  if (!response.ok) throw new Error("Failed to get batch status");
  return response.json();
}

export async function listCategories(): Promise<DocumentCategory[]> {
  const response = await fetch("/api/useradmin/categories");
  if (!response.ok) throw new Error("Failed to list categories");
  return response.json();
}

export async function createCategory(name: string, description?: string): Promise<DocumentCategory> {
  const response = await fetch("/api/useradmin/categories", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, description }),
  });
  if (!response.ok) throw new Error("Failed to create category");
  return response.json();
}
